import { type ValidationError } from '@wix/form-validator';
import { FORM_STATUS, type FormStatus } from '@wix/form-viewer';
import {
  type FormValues,
  FormViewer,
  type FormViewerHandle,
} from '@wix/form-viewer/widget';
import { Button } from '@wix/wix-ui-tpa/cssVars';
import {
  useBi,
  useTranslation,
  type WidgetProps,
} from '@wix/yoshi-flow-editor';
import React, { type FC, useRef, useState } from 'react';

import { AppLoaded } from '../../../shared/AppLoaded/AppLoaded';
import { FormPreview } from '../../../shared/FormPreview';
import { type ControllerProps } from '../../types';

import styles from './Form.module.scss';

export const Form: FC<WidgetProps<ControllerProps>> = ({
  appLoaded,
  setAppLoaded,
  submitForm,
  formId,
}) => {
  const formViewer = useRef<FormViewerHandle>(null);
  const [values, setValues] = useState<FormValues>({});
  const [previewValues, setPreviewValues] = useState({});
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [formStatus, setFormStatus] = useState<FormStatus>(FORM_STATUS.READY);
  const bi = useBi();
  const handleSubmit = async () => {
    if (!errors?.length) {
      setPreviewValues(values || {});
      submitForm(values);
    }
  };

  const handleValidate = async () => {
    await formViewer.current?.validate();
  };

  const { i18n } = useTranslation();

  return (
    <div className={styles.container} data-hook="SledTests-wrapper">
      <FormViewer
        i18n={i18n}
        ref={formViewer}
        formId={formId}
        values={values}
        errors={errors}
        onValidate={setErrors}
        onChange={setValues}
        bi={bi}
        onStatusChange={setFormStatus}
      />
      <Button
        data-hook="validate-button"
        className={styles.button}
        onClick={handleValidate}
      >
        Validate
      </Button>
      <Button
        disabled={formStatus === FORM_STATUS.BUSY}
        data-hook="external-submit-button"
        className={styles.button}
        onClick={handleSubmit}
      >
        Submit
      </Button>
      <div className={styles.previewContainer}>
        <FormPreview formId={formId} formValues={previewValues} i18n={i18n} />
      </div>
      <AppLoaded appLoaded={appLoaded} setAppLoaded={setAppLoaded} />
    </div>
  );
};
